// src/components/pos/ShoppingCart.jsx
import React, { useRef, useEffect, useState } from 'react';
import { Trash2, Plus, Minus, BookmarkPlus, BookOpen } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';

// 兜底 key，父组件没传 getItemKey 时用
const defaultItemKey = (item) =>
  `${item.product_id}-${item.sell_type || 'unit'}-${item.price_type || 'retail'}-${item.sku_id || 0}`;

const ShoppingCart = ({
  cartItems = [],
  getItemKey = defaultItemKey,
  onUpdateQuantity,
  onRemoveItem,
  onCheckout,
  onHold,
  onOpenPending,
  pendingCount = 0,
  paymentMethod = 'cash',
  onPaymentMethodChange
}) => {
  const { language } = useApp();
  const t = translations[language];
  const containerRef = useRef(null);
  const [cartListHeight, setCartListHeight] = useState(300);

  const getDisplayName = (item) => {
    if (language === 'en') return item.product_name_en || item.product_name;
    if (language === 'km') return item.product_name_km || item.product_name;
    return item.product_name;
  };

  useEffect(() => {
    const calculateHeight = () => {
      if (containerRef.current) {
        const containerHeight = containerRef.current.offsetHeight;
        const fixedHeight = 60 + 80 + 180 + 60 + 40;
        setCartListHeight(Math.max(containerHeight - fixedHeight, 200));
      }
    };
    calculateHeight();
    window.addEventListener('resize', calculateHeight);
    return () => window.removeEventListener('resize', calculateHeight);
  }, []);

  const totalAmount = Array.isArray(cartItems)
    ? cartItems.reduce((sum, item) => sum + (item.unit_price || 0) * (item.quantity || 0), 0)
    : 0;

  const totalItems = Array.isArray(cartItems)
    ? cartItems.reduce((sum, item) => sum + (item.quantity || 0), 0)
    : 0;

  const getSellTypeTag = (item) => {
    if (item.sell_type === 'box') {
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
          📦 整箱
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600">
        散装
      </span>
    );
  };

  const getPriceTypeTag = (item) => {
    if (item.price_type === 'vip') {
      return (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
          VIP
        </span>
      );
    }
    return null;
  };

  // 规格标签：有 sku_label 才显示
  const getSkuTag = (item) => {
    if (!item.sku_label) return null;
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700">
        {item.sku_label}
      </span>
    );
  };

  const getUnitLabel = (item) => {
    if (item.sell_type === 'box') {
      return `$${(item.unit_price || 0).toFixed(2)}/箱 × ${item.quantity}箱`;
    }
    return `$${(item.unit_price || 0).toFixed(2)}/个 × ${item.quantity}个`;
  };

  return (
    <div ref={containerRef} className="bg-white rounded-xl shadow-lg p-6 h-full flex flex-col">

      {/* 标题 + 挂单列表按钮 */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold">{t.cart}</h3>
        <button
          onClick={onOpenPending}
          className="relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 transition-colors">
          <BookOpen className="w-4 h-4" />
          <span>挂单</span>
          {pendingCount > 0 && (
            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* 购物车列表 */}
      <div className="overflow-y-auto mb-4" style={{ height: `${cartListHeight}px` }}>
        {!cartItems || cartItems.length === 0 ? (
          <div className="text-center text-gray-400 py-12">
            <p>{t.cartIsEmpty || '购物车是空的'}</p>
            <p className="text-sm mt-2">
              {language === 'en' ? 'Please select products to add' :
               language === 'km' ? 'សូមជ្រើសរើសទំនិញដើម្បីបន្ថែម' :
               '请选择商品添加'}
            </p>
          </div>
        ) : (
          <div className="space-y-3 pr-2">
            {cartItems.map((item) => {
              const key = getItemKey(item);
              return (
                <div key={key}
                  className="border rounded-lg p-3 hover:shadow-md transition-shadow">

                  <div className="flex justify-between items-start mb-1">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-800 text-sm truncate">{getDisplayName(item)}</h4>
                      <div className="flex items-center gap-1 mt-1 flex-wrap">
                        {getSkuTag(item)}
                        {getSellTypeTag(item)}
                        {getPriceTypeTag(item)}
                      </div>
                    </div>
                    <div className="text-right ml-2 flex-shrink-0">
                      <p className="font-bold text-blue-600 text-sm">
                        ${((item.unit_price || 0) * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 mb-2">{getUnitLabel(item)}</p>

                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => onUpdateQuantity && onUpdateQuantity(key, item.quantity - 1)}
                        className="p-1 rounded bg-gray-100 hover:bg-gray-200 transition-colors">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-10 text-center font-medium text-sm">{item.quantity}</span>
                      <button
                        onClick={() => onUpdateQuantity && onUpdateQuantity(key, item.quantity + 1)}
                        className="p-1 rounded bg-gray-100 hover:bg-gray-200 transition-colors">
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <button
                      onClick={() => onRemoveItem && onRemoveItem(key)}
                      className="text-red-500 hover:text-red-700 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 底部固定区域 */}
      <div className="border-t pt-4 space-y-4 mt-auto">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">商品数量:</span>
            <span className="font-medium">{totalItems} 件</span>
          </div>
          <div className="flex justify-between items-center text-lg font-bold">
            <span>{t.total}:</span>
            <span className="text-blue-600">${totalAmount.toFixed(2)}</span>
          </div>
        </div>

        {/* 支付方式 */}
        <div className="border-t pt-3">
          <h4 className="font-semibold mb-2 text-sm">{t.paymentMethod}</h4>
          <div className="space-y-1">
            {[
              { value: 'cash',   label: t.cash },
              { value: 'card',   label: t.card },
              { value: 'mobile', label: t.mobile },
            ].map(opt => (
              <label key={opt.value} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors">
                <input type="radio" value={opt.value}
                  checked={paymentMethod === opt.value}
                  onChange={e => onPaymentMethodChange && onPaymentMethodChange(e.target.value)}
                  className="mr-2" />
                <span className="text-sm">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* 结算 + 挂单 按钮 */}
        <div className="flex gap-2">
          <button
            onClick={onHold}
            disabled={!cartItems || cartItems.length === 0}
            className={`flex items-center justify-center gap-1.5 px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
              cartItems && cartItems.length > 0
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 cursor-pointer'
                : 'bg-gray-100 text-gray-300 cursor-not-allowed'
            }`}>
            <BookmarkPlus className="w-4 h-4" />
            挂单
          </button>

          <button
            onClick={onCheckout}
            disabled={!cartItems || cartItems.length === 0}
            className={`flex-1 py-3 rounded-lg font-semibold text-white transition-colors ${
              cartItems && cartItems.length > 0
                ? 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                : 'bg-gray-300 cursor-not-allowed'
            }`}>
            {cartItems && cartItems.length > 0 ? t.checkout : '请先添加商品'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShoppingCart;